import { Preferences } from "@capacitor/preferences";
import { MEALS_COLLECTION, type MealsConfigurationDocType } from "@macro-tracker/models";
import { getDbForCurrentUser, } from "@/services/rxdb/database";
import type { Meal, MealId, MealType } from '@macro-tracker/models/validators';


export type MealsConfiguration = {
  id: string;
  meals: Meal[];
  updatedAt: string;
};

const getMealsKey = async (): Promise<string> => {
  const { useAuthSessionStore } = await import('@/stores/authSessionStore');
  const userId = useAuthSessionStore().user?.id ?? 'anonymous';
  return `macrotracker:meals:${userId}`;
};

const getCurrentUserId = async (): Promise<string> => {
  const { useAuthSessionStore } = await import('@/stores/authSessionStore');
  return useAuthSessionStore().user?.id ?? 'anonymous';
};

const getMealsDocId = (userId: string): string => `${userId}:meals`;

const DefaultMealTitleForId = (id: MealId): string => {
  switch (id) {
    case 0:
      return "Breakfast";
    case 1:
      return "Lunch";
    case 2:
      return "Dinner";
    case 3:
      return "Snack";
    default:
      return `Meal ${id + 1}`;
  }
};

export const DefaultMealTypeForId = (id: MealId): MealType =>
  id === 3 ? "snack" : "meal";

export const DefaultMealTimeRangeForId = (id: MealId): { startTime?: string; endTime?: string } => {
  switch (id) {
    case 0: // Breakfast
      return { startTime: "06:00", endTime: "10:00" };
    case 1: // Lunch
      return { startTime: "11:00", endTime: "14:00" };
    case 2: // Dinner
      return { startTime: "17:00", endTime: "21:00" };
    case 3: // Snack
      return { startTime: undefined, endTime: undefined };
    default:
      return { startTime: undefined, endTime: undefined };
  }
};

export const defaultMealsConfiguration = (): MealsConfiguration => ({
  id: 'meals',
  updatedAt: new Date().toISOString(),
  meals: (Array.from({ length: 10 }, (_, index) => {
    const id = index as MealId;
    const timeRange = DefaultMealTimeRangeForId(id);
    return {
      id,
      title: DefaultMealTitleForId(id),
      enabled: id <= 3,
      displayOrder: id,
      mealType: DefaultMealTypeForId(id),
      ...timeRange
    };
  }) satisfies Meal[]).slice(),
});

type MealRange = {
  startMinutes: number | null;
  endMinutes: number | null;
};

type ResolvedMealRange = {
  meal: Meal;
  range: MealRange;
};

type DefaultMealOptions = {
  now?: Date;
  timeZone?: string;
};

const MINUTES_IN_DAY = 24 * 60;

const parseTimeToMinutes = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }

  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return (hours * 60 + minutes) % MINUTES_IN_DAY;
};

const normalizeMealType = (value: unknown, id: MealId, title: string): MealType => {
  if (value === "meal" || value === "snack") {
    return value;
  }
  if (/\bsnack\b/i.test(title)) {
    return "snack";
  }
  return DefaultMealTypeForId(id);
};

const getCurrentMinutesInTimeZone = (now: Date, timeZone?: string): number => {
  if (!timeZone) {
    return now.getHours() * 60 + now.getMinutes();
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });
    const parts = formatter.formatToParts(now);
    const hourPart = parts.find((part) => part.type === "hour")?.value;
    const minutePart = parts.find((part) => part.type === "minute")?.value;
    const hours = Number(hourPart);
    const minutes = Number(minutePart);

    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return (hours * 60 + minutes) % MINUTES_IN_DAY;
    }
  } catch {
    // Fall back to local device time if timezone parsing fails.
  }

  return now.getHours() * 60 + now.getMinutes();
};

const resolveMealRange = (meal: Meal): MealRange => {
  const hasCustomRange = meal.startTime !== undefined || meal.endTime !== undefined;
  const fallbackRange: { startTime?: string; endTime?: string } = hasCustomRange
    ? {}
    : DefaultMealTimeRangeForId(meal.id);

  return {
    startMinutes: parseTimeToMinutes(meal.startTime ?? fallbackRange.startTime),
    endMinutes: parseTimeToMinutes(meal.endTime ?? fallbackRange.endTime),
  };
};

const isWithinMealRange = (minutes: number, range: MealRange): boolean => {
  const { startMinutes, endMinutes } = range;

  if (startMinutes !== null && endMinutes !== null) {
    if (startMinutes === endMinutes) {
      return true;
    }
    if (startMinutes < endMinutes) {
      return minutes >= startMinutes && minutes < endMinutes;
    }
    return minutes >= startMinutes || minutes < endMinutes;
  }

  if (startMinutes !== null) {
    return minutes >= startMinutes;
  }

  if (endMinutes !== null) {
    return minutes < endMinutes;
  }

  return false;
};

export const getDefaultMealIdForCurrentTime = (
  meals: Meal[],
  options: DefaultMealOptions = {},
): MealId | null => {
  const enabledMeals = getEnabledMeals(meals)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (enabledMeals.length === 0) {
    return null;
  }

  const now = options.now ?? new Date();
  const resolvedTimeZone = options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentMinutes = getCurrentMinutesInTimeZone(now, resolvedTimeZone);

  const resolvedMeals: ResolvedMealRange[] = enabledMeals.map((meal) => ({
    meal,
    range: resolveMealRange(meal),
  }));

  const rangeMatch = resolvedMeals.find(
    ({ range }) =>
      (range.startMinutes !== null || range.endMinutes !== null) &&
      isWithinMealRange(currentMinutes, range),
  );
  if (rangeMatch) {
    return rangeMatch.meal.id;
  }

  const timedMeals = resolvedMeals.filter(
    ({ range }) => range.startMinutes !== null || range.endMinutes !== null,
  );
  if (timedMeals.length === 0) {
    return enabledMeals[0]?.id ?? null;
  }

  const nextTimedMeal = timedMeals
    .filter(({ range }) => range.startMinutes !== null && range.startMinutes > currentMinutes)
    .sort((a, b) => {
      const startDifference = (a.range.startMinutes ?? MINUTES_IN_DAY) - (b.range.startMinutes ?? MINUTES_IN_DAY);
      if (startDifference !== 0) {
        return startDifference;
      }
      return a.meal.displayOrder - b.meal.displayOrder;
    })[0];
  if (nextTimedMeal) {
    return nextTimedMeal.meal.id;
  }

  const lastTimedMeal = timedMeals
    .filter(({ range }) => range.startMinutes !== null)
    .sort((a, b) => {
      const startDifference = (b.range.startMinutes ?? -1) - (a.range.startMinutes ?? -1);
      if (startDifference !== 0) {
        return startDifference;
      }
      return b.meal.displayOrder - a.meal.displayOrder;
    })[0];

  return lastTimedMeal?.meal.id ?? timedMeals[timedMeals.length - 1]?.meal.id ?? enabledMeals[0]?.id ?? null;
};

export const asMealId = (value: unknown): MealId | null => {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return null;
  }
  return value >= 0 && value <= 9 ? (value as MealId) : null;
};

const normalizeMeals = (candidate: unknown): Meal[] => {
  const defaults = defaultMealsConfiguration().meals;

  if (!Array.isArray(candidate)) {
    return defaults;
  }

  const byId = new Map<MealId, Meal>();
  for (let i = 0; i < candidate.length; i++) {
    const row = candidate[i];
    const id = asMealId((row as any)?.id);
    if (id === null) {
      continue;
    }
    const title =
      typeof (row as any)?.title === "string" && (row as any).title.trim()
        ? (row as any).title.trim()
        : DefaultMealTitleForId(id);
    const enabled = Boolean((row as any)?.enabled);
    const mealType = normalizeMealType((row as any)?.mealType, id, title);

    let displayOrder = Number(row.displayOrder) ?? i
    if (!isFinite(displayOrder)) {
      displayOrder = i;
    }
    const startTime = typeof (row as any)?.startTime === 'string' ? (row as any).startTime : undefined;
    const endTime = typeof (row as any)?.endTime === 'string' ? (row as any).endTime : undefined;
    byId.set(id, { id, title, enabled, displayOrder, mealType, startTime, endTime });
  }

  const meals = defaults.map((meal) => byId.get(meal.id) ?? meal);

  // Enforce enabled meals first (contiguous), by clamping enabled count.
  const enabledCount = meals.filter((meal) => meal.enabled).length;
  return meals.map((meal) => ({
    ...meal,
    enabled: meal.id < enabledCount,
  }));
};

export const loadMealsConfiguration = async (): Promise<MealsConfiguration> => {
  const userId = await getCurrentUserId();
  const db = await getDbForCurrentUser();
  const docId = getMealsDocId(userId);
  const existingDoc = await db[MEALS_COLLECTION].findOne(docId).exec();
  if (existingDoc) {
    const doc = existingDoc.toMutableJSON() as MealsConfigurationDocType;
    const normalizedMeals = normalizeMeals(doc.meals);
    if (JSON.stringify(doc.meals) !== JSON.stringify(normalizedMeals)) {
      await existingDoc.incrementalModify((d: MealsConfigurationDocType) => {
        d.meals = normalizedMeals;
        d.updatedAt = new Date().toISOString();
        return d;
      });
    }
    const config = {
      id: 'meals',
      meals: normalizedMeals,
      updatedAt: doc.updatedAt,
    };
    await Preferences.set({ key: await getMealsKey(), value: JSON.stringify(config) });
    return config;
  }

  const key = await getMealsKey();
  const { value } = await Preferences.get({ key });
  if (value) {
    try {
      const parsed = JSON.parse(value) as Partial<MealsConfiguration>;
      const normalizedMeals = normalizeMeals(parsed.meals);
      const normalized: MealsConfiguration = {
        id: 'meals',
        meals: normalizedMeals,
        updatedAt: parsed.updatedAt ?? new Date().toISOString(),
      };
      if (JSON.stringify(parsed.meals) !== JSON.stringify(normalizedMeals)) {
        await Preferences.set({ key, value: JSON.stringify({ ...normalized, updatedAt: new Date().toISOString() }) });
      }
      const now = new Date().toISOString();
      await db[MEALS_COLLECTION].upsert({
        id: docId,
        userId,
        meals: normalizedMeals,
        createdAt: parsed.updatedAt ?? now,
        updatedAt: parsed.updatedAt ?? now,
        deletedAt: null,
      });
      return normalized;
    } catch {
      // fall through to default
    }
  }
  const created = defaultMealsConfiguration();
  await Preferences.set({ key, value: JSON.stringify(created) });
  await db[MEALS_COLLECTION].upsert({
    id: docId,
    userId,
    meals: created.meals,
    createdAt: created.updatedAt,
    updatedAt: created.updatedAt,
    deletedAt: null,
  });
  return created;
};

export const saveMealsConfiguration = async (meals: Meal[]): Promise<void> => {
  const userId = await getCurrentUserId();
  const db = await getDbForCurrentUser();
  const key = await getMealsKey();
  const normalizedMeals = normalizeMeals(meals);
  const config: MealsConfiguration = {
    id: 'meals',
    meals: normalizedMeals,
    updatedAt: new Date().toISOString(),
  };
  await Preferences.set({ key, value: JSON.stringify(config) });
  const docId = getMealsDocId(userId);
  const existingDoc = await db[MEALS_COLLECTION].findOne(docId).exec();
  if (existingDoc) {
    await existingDoc.incrementalModify((d: MealsConfigurationDocType) => {
      d.meals = normalizedMeals;
      d.updatedAt = config.updatedAt;
      d.deletedAt = null;
      return d;
    });
  } else {
    await db[MEALS_COLLECTION].insert({
      id: docId,
      userId,
      meals: normalizedMeals,
      createdAt: config.updatedAt,
      updatedAt: config.updatedAt,
      deletedAt: null,
    });
  }
};

export const resetMealsConfiguration = async (): Promise<MealsConfiguration> => {
  const userId = await getCurrentUserId();
  const db = await getDbForCurrentUser();
  const key = await getMealsKey();
  const created = defaultMealsConfiguration();
  await Preferences.set({ key, value: JSON.stringify(created) });
  await db[MEALS_COLLECTION].upsert({
    id: getMealsDocId(userId),
    userId,
    meals: created.meals,
    createdAt: created.updatedAt,
    updatedAt: created.updatedAt,
    deletedAt: null,
  });
  return created;
};

export const getEnabledMeals = (meals: Meal[]): Meal[] =>
  meals.filter((meal) => meal.enabled);

export const getMealTitle = (meals: Meal[], mealId: MealId): string =>
  meals.find((meal) => meal.id === mealId)?.title ?? DefaultMealTitleForId(mealId);
